import axios from "axios";

// En dev Vite proxia /api al backend (ver vite.config.ts).
// En prod, nginx (o Traefik) hace de reverse proxy.
export const apiClient = axios.create({
  baseURL: "/api",
  timeout: 15_000,
  headers: { "Content-Type": "application/json" },
  // CRITICO (TASK-005): la sesion vive en una cookie HttpOnly. Sin esto el
  // navegador NO la envia en peticiones cross-origin/dev y la API responde 401
  // aunque el usuario haya hecho login.
  withCredentials: true,
});

/**
 * Rutas donde un 401 NO significa "sesion caida" y no hay que redirigir:
 * el login mismo (credenciales malas) y /me (sondeo inicial de sesion, que
 * responde 401 cuando todavia no hay usuario).
 */
const SIN_REDIRECT_401 = ["/auth/login", "/auth/logout", "/auth/me"];

// Interceptor de respuesta: si la sesion cayo, volver al login una sola vez.
apiClient.interceptors.response.use(
  (respuesta) => respuesta,
  (error) => {
    const status = error?.response?.status;
    const url: string = error?.config?.url ?? "";

    const esRutaDeSondeo = SIN_REDIRECT_401.some((u) => url.includes(u));
    if (status === 401 && !esRutaDeSondeo) {
      // Evitar bucles: si ya estamos en /login, no redirigir de nuevo.
      if (!window.location.pathname.startsWith("/login")) {
        const destino = window.location.pathname + window.location.search;
        window.location.href = `/login?next=${encodeURIComponent(destino)}`;
      }
    }
    return Promise.reject(error);
  }
);

export async function healthcheck(): Promise<{ status: string }> {
  const { data } = await apiClient.get("/health");
  return data;
}

/**
 * `fetch` con la cookie de sesion incluida.
 *
 * POR QUE EXISTE:
 *   El codigo de los tableros usa `fetch('/api/...')` directamente (23
 *   llamadas). Desde TASK-003 la API exige sesion, y `fetch` SIN
 *   `credentials: 'include'` NO envia la cookie: todas esas llamadas darian
 *   401 y los tableros quedarian vacios.
 *
 *   En vez de reescribir 23 llamadas (y arriesgar romper JSX en cada una), se
 *   expone este wrapper que conserva la firma de `fetch` y solo agrega la
 *   credencial. Codigo nuevo: preferir `apiClient`.
 *
 * Uso identico a fetch:
 *   const res = await apiFetch(`/api/operaciones/resumen?${params}`)
 */
export function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return fetch(input, { credentials: "include", ...(init ?? {}) });
}
